import AppConfirmationModal from '@nxsys/components/AppConfirmationModal';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import React, { useRef, useState } from 'react';
import WorkingPatternForm, {
  WorkingPatternFormRef,
  WorkingPatternValues,
} from './WorkingPatternForm';
import { WORKING_PATTERN_OPTIONS } from './workingPatternOptions';

// NX-4948: same starting values as the Old UI (ModalAddNewWorkingPattern):
// a standard Mon–Fri, 5-day, 40-hour week, not the company default.
const defaultValues: Partial<WorkingPatternValues> = {
  isDefault: false,
  workingDaysFTE: '5',
  workingDays: '5',
  workingHoursFTE: '40',
  workingHours: '40',
  workingPattern: WORKING_PATTERN_OPTIONS.slice(0, 5),
};

type CreateWorkingPatternModal = {
  isOpen: boolean;
  toggle: () => void;
  onSubmit: (params: WorkingPatternValues) => Promise<void>;
};

const CreateWorkingPatternModal: React.FC<CreateWorkingPatternModal> = ({
  isOpen,
  toggle,
  onSubmit,
}: CreateWorkingPatternModal) => {
  const { messages } = useTypedIntl();
  const formRef = useRef<WorkingPatternFormRef>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  // NX-4948: like the Old UI, setting a pattern as the company default asks
  // first — every employee without their own pattern moves to it.
  const [pendingDefault, setPendingDefault] =
    useState<WorkingPatternValues | null>(null);

  const save = async (values: WorkingPatternValues) => {
    try {
      setIsSubmitting(true);
      await onSubmit(values);
      formRef.current?.reset();
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (values: WorkingPatternValues) => {
    if (values.isDefault) {
      setPendingDefault(values);
      return;
    }
    await save(values);
  };

  // Closing discards what was typed so the next open starts from the
  // defaults again (Old UI parity).
  const handleClose = () => {
    formRef.current?.reset();
    toggle();
  };

  return (
    <AppModalContainer
      title={messages['common.addNewWorkingPattern']}
      isOpen={isOpen}
      toggle={handleClose}
      actions={[
        {
          name: messages['common.cancel'],
          variant: 'outlined',
          onClick: handleClose,
        },
        {
          name: messages['common.save'],
          buttonType: 'primary',
          onClick: () => formRef.current?.submit(),
          loading: isSubmitting,
          disabled: isSubmitting,
        },
      ]}
      minHeight="200px"
    >
      <WorkingPatternForm
        formRef={formRef}
        onSubmit={handleSubmit}
        defaultValues={defaultValues}
      />
      <AppConfirmationModal
        open={!!pendingDefault}
        icon={null}
        title={messages['common.setAsWorkingPatternDefault']}
        paragraph={`Are you sure you want to set the working pattern "${pendingDefault?.name ?? ''}" as default?`}
        loading={isSubmitting}
        onDeny={() => setPendingDefault(null)}
        onConfirm={async () => {
          if (!pendingDefault) return;
          await save(pendingDefault);
          setPendingDefault(null);
        }}
      />
    </AppModalContainer>
  );
};

export default CreateWorkingPatternModal;
